import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import MapIcon from "@mui/icons-material/Map";
import { DataGrid } from "@mui/x-data-grid";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import axios from "axios";

export default function AdmittedBatchProgramMapping() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [batches, setBatches] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [mappings, setMappings] = useState([]); // existing mappings from DB
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState("ALL");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const user = useMemo(() => {
    const u = sessionStorage.getItem("user");
    return u ? JSON.parse(u) : null;
  }, []);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setSnackbarOpen(true);
  };

  // Fetch active admitted batches for the current college
  const fetchBatches = useCallback(async () => {
    if (!user) return;
    try {
      // server exposes admitted batches under /api/admittedbatches
      const url = `${API_BASE_URL}/api/admittedbatches/${user.client_id}/${user.college_id}?status=ACTIVE`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      const data =
        res.data && res.data.items ? res.data.items : Array.isArray(res.data) ? res.data : [];
      setBatches(data || []);
    } catch (err) {
      console.error("fetchBatches", err);
      showSnackbar("Failed to load admitted batches", "error");
      setBatches([]);
    }
  }, [user, API_BASE_URL]);

  // Fetch active programs
  const fetchPrograms = useCallback(async () => {
    if (!user) return;
    try {
      // server exposes programs under /api/programs
      const url = `${API_BASE_URL}/api/programs/${user.client_id}/${user.college_id}?status=ACTIVE`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      const data =
        res.data && (Array.isArray(res.data) ? res.data : res.data.data)
          ? Array.isArray(res.data)
            ? res.data
            : res.data.data
          : [];
      setPrograms(data || []);
    } catch (err) {
      console.error("fetchPrograms", err);
      showSnackbar("Failed to load programs", "error");
      setPrograms([]);
    }
  }, [user, API_BASE_URL]);

  // Fetch existing mappings
  const fetchMappings = useCallback(async () => {
    if (!user) return;
    try {
      const url = `${API_BASE_URL}/api/map_admittedbatch_program/${user.client_id}/${user.college_id}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      const data =
        res.data && (Array.isArray(res.data) ? res.data : res.data.data)
          ? Array.isArray(res.data)
            ? res.data
            : res.data.data
          : [];
      setMappings(data || []);
    } catch (err) {
      console.error("fetchMappings", err);
      showSnackbar("Failed to load mappings", "error");
      setMappings([]);
    }
  }, [user, API_BASE_URL]);

  useEffect(() => {
    if (user) {
      setLoading(true);
      Promise.all([fetchBatches(), fetchPrograms(), fetchMappings()])
        .catch((e) => console.error(e))
        .finally(() => setLoading(false));
    }
  }, [user, fetchBatches, fetchPrograms, fetchMappings]);

  // Build grid rows: cross join batches x programs and mark mapped
  useEffect(() => {
    const newRows = [];
    let idx = 1;
    batches.forEach((b) => {
      programs.forEach((p) => {
        // find mapping
        const map = mappings.find(
          (m) =>
            Number(m.admittedbatch_id) === Number(b.admittedbatch_id) &&
            Number(m.program_id) === Number(p.program_id)
        );
        newRows.push({
          id: `${b.admittedbatch_id}_${p.program_id}`,
          sn: idx++,
          admittedbatch_id: b.admittedbatch_id,
          admittedbatch: b.admittedbatch,
          program_id: p.program_id,
          program_name: p.program_name || p.program || "",
          program_description: p.description || "",
          mapped: Boolean(map),
          mapping_id: map ? map.mapping_id || map.id || null : null,
        });
      });
    });
    setRows(newRows);
  }, [batches, programs, mappings]);

  // Toggle mapping (auto-updates immediately)
  const handleToggle = async (row) => {
    if (!user) return showSnackbar("User not authenticated", "error");
    try {
      // optimistic update
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, mapped: !r.mapped } : r)));

      if (!row.mapped) {
        // create mapping
        const payload = {
          client_id: user.client_id,
          college_id: user.college_id,
          admittedbatch_id: row.admittedbatch_id,
          program_id: row.program_id,
          created_by: user.user_email || user.user_email_id || null,
        };
        const res = await axios.post(`${API_BASE_URL}/api/map_admittedbatch_program`, payload, {
          headers: getAuthHeaders(),
        });
        const created = res.data && (res.data.data || res.data) ? res.data.data || res.data : null;
        // update mapping id if returned
        if (created && created.mapping_id) {
          setRows((prev) =>
            prev.map((r) => (r.id === row.id ? { ...r, mapping_id: created.mapping_id } : r))
          );
        }
        showSnackbar(res.data.message || "Mapped successfully", "success");
        // refresh mappings to keep consistent
        fetchMappings();
      } else {
        // unmap: try delete by mapping id if available, otherwise send delete payload
        let res;
        if (row.mapping_id) {
          // include client_id in body so server can locate the correct client's DB
          res = await axios.delete(
            `${API_BASE_URL}/api/map_admittedbatch_program/${row.mapping_id}`,
            {
              headers: getAuthHeaders(),
              data: { client_id: user.client_id },
            }
          );
        } else {
          // fallback: send body to delete endpoint
          res = await axios.request({
            url: `${API_BASE_URL}/api/map_admittedbatch_program`,
            method: "delete",
            headers: getAuthHeaders(),
            data: {
              client_id: user.client_id,
              college_id: user.college_id,
              admittedbatch_id: row.admittedbatch_id,
              program_id: row.program_id,
            },
          });
        }
        showSnackbar(res.data?.message || "Unmapped successfully", "success");
        fetchMappings();
      }
    } catch (err) {
      console.error("toggle mapping", err);
      const errorMessage = err.response?.data?.message || "Failed to update mapping";
      showSnackbar(errorMessage, "error");
      // revert optimistic
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, mapped: row.mapped } : r)));
    }
  };

  const columns = [
    { field: "sn", headerName: "S.No", width: 80 },
    { field: "admittedbatch", headerName: "Admitted Batch", flex: 1, minWidth: 180 },
    { field: "program_name", headerName: "Program Name", flex: 1, minWidth: 220 },
    { field: "program_description", headerName: "Description", flex: 1.2, minWidth: 240 },
    {
      field: "mapped",
      headerName: "Map/Unmap",
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <FormControlLabel
          control={<Switch checked={params.row.mapped} onChange={() => handleToggle(params.row)} />}
          label={params.row.mapped ? "Mapped" : "Unmapped"}
        />
      ),
    },
  ];

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            ADMITTED BATCH - PROGRAM MAPPING
          </Typography>
          {/* Select admitted batch filter + program select + search box */}
          <TextField
            label="Select Admitted Batch"
            name="admitted_batch"
            select
            size="medium"
            value={selectedBatchId}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true, displayEmpty: true }}
            onChange={(e) => setSelectedBatchId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <option value="ALL">-- All Batches --</option>
            {batches.map((b) => (
              <option key={b.admittedbatch_id} value={String(b.admittedbatch_id)}>
                {b.admittedbatch}
              </option>
            ))}
          </TextField>
        </Toolbar>

        <Box
          ml={2}
          mr={2}
          sx={{
            height: 560,
          }}
        >
          <DataGrid
            rows={useMemo(() => {
              return rows.filter((r) => {
                if (
                  selectedBatchId &&
                  selectedBatchId !== "ALL" &&
                  String(r.admittedbatch_id) !== String(selectedBatchId)
                )
                  return false;
                return true;
              });
            }, [rows, selectedBatchId])}
            columns={columns}
            loading={loading}
            pagination
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            disableSelectionOnClick
            getRowId={(r) => r.id}
            sx={{
              "& .MuiDataGrid-cell": { borderBottom: "1px solid #e0e0e0" },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                borderBottom: "2px solid #e0e0e0",
              },
            }}
          />
        </Box>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
